/**
 * Iter-63.1.12A — Frontend applicability definition for the
 * `at_fault_party_insured` (AFPI) MVA question.
 *
 * SOURCE OF TRUTH for the backend is `backend/mva_schema.MVA_CASE_TYPES`.
 * Because the Python set cannot be imported directly we mirror it here as
 * a small module the three public forms + Hosted form share, and lock the
 * two sets with a parity regression test
 * (`frontend/src/__tests__/iter63_1_12a_mvaApplicabilityParity.test.js`).
 *
 * The set intentionally lists BOTH canonical values (`auto`, `motorcycle`,
 * …) and the raw funnel-form value `car` which we normalise to `auto` at
 * submission time — that way the applicability check works regardless of
 * whether the caller feeds us the raw funnel selection or the resolved
 * canonical accident type.
 */

// Canonical MVA types — every value in this Set MUST be a member of
// backend/mva_schema.MVA_CASE_TYPES.
export const CANONICAL_MVA_CASE_TYPES = Object.freeze([
  "auto",
  "motorcycle",
  "truck",
  "commercial_vehicle",
  "rideshare",
  "public_transportation",
  "pedestrian",
  "bicycle",
]);

// Focused Funnel raw accident_type values that MAP to canonical MVA types.
// `car` normalises to `auto` at the backend intake layer
// (`routes/public_routes._CASE_TYPE_TO_ACCIDENT_TYPE`).
const FRONTEND_MVA_ALIASES = Object.freeze(["car"]);

const MVA_LOOKUP = new Set([
  ...CANONICAL_MVA_CASE_TYPES,
  ...FRONTEND_MVA_ALIASES,
]);

// Landing-page slug → canonical backend accident_type. Mirrors
// backend/routes/public_routes._LANDING_SLUG_TO_ACCIDENT_TYPE. `personal-injury`
// and `free-case-review` map to `other` because the concrete accident type
// is refined by the visitor's case_type selection.
const SLUG_TO_ACCIDENT_TYPE = Object.freeze({
  "auto-accident": "auto",
  "truck-accident": "truck",
  "rideshare-accident": "rideshare",
  "motorcycle-accident": "motorcycle",
  "personal-injury": "other",
  "free-case-review": "other",
});

export function isMvaCaseType(value) {
  if (value === null || value === undefined) return false;
  const s = String(value).trim().toLowerCase();
  return s.length > 0 && MVA_LOOKUP.has(s);
}

export function slugToAccidentType(slug) {
  if (!slug) return null;
  const key = String(slug).trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(SLUG_TO_ACCIDENT_TYPE, key)
    ? SLUG_TO_ACCIDENT_TYPE[key] : null;
}

/**
 * Resolve the effective accident_type for AFPI applicability given the
 * ambient context. Precedence:
 *
 *   1. `accidentType` (explicit override from a caller who already knows it).
 *   2. `caseType`     (Standard PI/FCR dropdown OR Focused Funnel selection).
 *   3. `verticalSlug` (Standard vertical page whose accident_type is
 *                       implied by the slug).
 */
export function resolveAccidentType({ verticalSlug, caseType, accidentType } = {}) {
  const pick = (v) => (v ? String(v).trim().toLowerCase() : "");
  const explicit = pick(accidentType);
  if (explicit) return explicit;
  const ct = pick(caseType);
  if (ct) return ct;
  return slugToAccidentType(verticalSlug);
}

export function requiresAtFaultPartyInsured(context) {
  return isMvaCaseType(resolveAccidentType(context || {}));
}

// Canonical tri-state values sent to the backend.
export const AFPI_VALUES = Object.freeze({
  YES: "yes",
  NO: "no",
  UNKNOWN: "unknown",
});

export const AFPI_CHOICES = Object.freeze([
  { value: AFPI_VALUES.YES,     label: "Yes" },
  { value: AFPI_VALUES.NO,      label: "No" },
  { value: AFPI_VALUES.UNKNOWN, label: "I'm Not Sure" },
]);
